/**
 * Pub/sub for the PWA service-worker update state. Same pattern as
 * `expiredLinkModalService.ts` — a module singleton + listeners so any
 * code path can react to "an update is waiting to apply" without
 * prop-drilling through React tree.
 *
 * The state is driven by `<PwaUpdateInitializer />`, which owns the
 * `useRegisterSW()` hook from vite-plugin-pwa and pushes updates here.
 * `<UpdateAvailableBanner />` subscribes and renders based on the flag.
 *
 * `updateAndReload` calls into vite-plugin-pwa's `updateServiceWorker(true)`,
 * which:
 *   1. Sends SKIP_WAITING to the waiting SW so it activates immediately.
 *   2. Awaits `controllerchange` (the page is now controlled by the new SW).
 *   3. Reloads the page so all assets are re-fetched through the new SW.
 *
 * Until the initializer mounts and pushes a real callback, we fall back
 * to a plain `window.location.reload()` — defensive, banner clicks should
 * never happen before initialization in practice, but better than throwing.
 */
type State = {
  needRefresh: boolean;
  updateAndReload: () => Promise<void>;
};

type Listener = (state: State) => void;

let state: State = {
  needRefresh: false,
  updateAndReload: async () => {
    window.location.reload();
  },
};

const listeners: Set<Listener> = new Set();

function notify() {
  listeners.forEach((l) => l(state));
}

export function setPwaUpdateState(next: Partial<State>) {
  state = { ...state, ...next };
  notify();
}

export function getPwaUpdateState(): State {
  return state;
}

export function subscribeToPwaUpdate(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}
